import React, { useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  Sparkles, FileText, Link2, Loader2,
  AlertTriangle, Newspaper, Rss,
} from "lucide-react";

import AdminSignIn from "../components/admin/AdminSignIn";
import AdminPendingList from "../components/admin/AdminPendingList";
import AdminTakedownsList from "../components/admin/AdminTakedownsList";
import AdminCampaignsList from "../components/admin/AdminCampaignsList";
import AdminTipsList from "../components/admin/AdminTipsList";
import AdminPartnerImportsList from "../components/admin/AdminPartnerImportsList";
import AdminPhotoEditor from "../components/admin/AdminPhotoEditor";
import AdminPhotoCoverage from "../components/admin/AdminPhotoCoverage";
import AdminPushToggle from "../components/admin/AdminPushToggle";
import AdminAnalytics from "../components/admin/AdminAnalytics";
import AdminActiveCasesList from "../components/admin/AdminActiveCasesList";
import AdminUrgentRescuesList from "../components/admin/AdminUrgentRescuesList";
import AdminRescuePartnersList from "../components/admin/AdminRescuePartnersList";
import AdminSheltersList from "../components/admin/AdminSheltersList";
import SEO from "../components/SEO";

const TOKEN_KEY = "paws_admin_token";

// Prefer sessionStorage (cleared on tab close) over localStorage to reduce
// XSS blast radius — admin token is session-scoped only.
const tokenStore = {
  get: () => {
    try {
      return window.sessionStorage.getItem(TOKEN_KEY) || "";
    } catch {
      return "";
    }
  },
  set: (v) => {
    try {
      window.sessionStorage.setItem(TOKEN_KEY, v);
    } catch {
      /* storage may be blocked in private mode */
    }
  },
  clear: () => {
    try {
      window.sessionStorage.removeItem(TOKEN_KEY);
      window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* ignore */
    }
  },
};

export default function Admin() {
  const [token, setToken] = useState(() => tokenStore.get());
  const [authed, setAuthed] = useState(false);
  const [authTry, setAuthTry] = useState("");
  const [authLoading, setAuthLoading] = useState(false);

  const [mode, setMode] = useState("url");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [ingesting, setIngesting] = useState(false);

  const [pending, setPending] = useState([]);
  const [loadingList, setLoadingList] = useState(false);

  const [takedowns, setTakedowns] = useState([]);
  const [loadingTakedowns, setLoadingTakedowns] = useState(false);

  const [tips, setTips] = useState([]);
  const [loadingTips, setLoadingTips] = useState(false);
  const [ingestingTipId, setIngestingTipId] = useState(null);

  const [partnerImports, setPartnerImports] = useState([]);
  const [loadingPartnerImports, setLoadingPartnerImports] = useState(false);

  const [rssRunning, setRssRunning] = useState(false);

  const headers = { "X-Admin-Token": token };

  const loadPending = useCallback(async () => {
    if (!token) return;
    setLoadingList(true);
    try {
      const { data } = await api.get("/admin/pending", {
        headers: { "X-Admin-Token": token },
      });
      setPending(data.items || []);
    } catch {
      toast.error("Failed to load queue.");
    } finally {
      setLoadingList(false);
    }
  }, [token]);

  const loadTakedowns = useCallback(async () => {
    if (!token) return;
    setLoadingTakedowns(true);
    try {
      const { data } = await api.get("/admin/takedowns", {
        headers: { "X-Admin-Token": token },
      });
      setTakedowns(data.items || []);
    } catch {
      toast.error("Failed to load takedowns.");
    } finally {
      setLoadingTakedowns(false);
    }
  }, [token]);

  const loadTips = useCallback(async () => {
    if (!token) return;
    setLoadingTips(true);
    try {
      const { data } = await api.get("/admin/registry-tips", {
        headers: { "X-Admin-Token": token },
      });
      setTips(data.items || []);
    } catch {
      toast.error("Failed to load tips.");
    } finally {
      setLoadingTips(false);
    }
  }, [token]);

  const loadPartnerImports = useCallback(async () => {
    if (!token) return;
    setLoadingPartnerImports(true);
    try {
      const { data } = await api.get("/admin/partners/imports", {
        headers: { "X-Admin-Token": token },
      });
      setPartnerImports(data.items || []);
    } catch {
      toast.error("Failed to load partner imports.");
    } finally {
      setLoadingPartnerImports(false);
    }
  }, [token]);

  const resolveTip = async (id) => {
    try {
      await api.post(
        `/admin/registry-tips/${id}/resolve`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      toast.success("Tip resolved.");
      loadTips();
    } catch {
      toast.error("Failed to resolve tip.");
    }
  };

  const claudeIngestTip = async (tip) => {
    setIngestingTipId(tip.id);
    try {
      const { data } = await api.post(
        "/ingest/article",
        {
          url: tip.source_url,
          tipster_email: tip.tipster_email || null,
          tipster_tip_id: tip.id,
        },
        { headers: { "X-Admin-Token": token } },
      );
      toast.success(
        `Claude extracted ${data.extracted} case${data.extracted === 1 ? "" : "s"}. Review the queue.`,
      );
      // Resolve the tip and refresh the pending queue
      await api.post(
        `/admin/registry-tips/${tip.id}/resolve`,
        {},
        { headers: { "X-Admin-Token": token } },
      );
      loadTips();
      loadPending();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Claude ingest failed.";
      toast.error(msg);
    } finally {
      setIngestingTipId(null);
    }
  };

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        await api.get("/admin/ping", { headers: { "X-Admin-Token": token } });
        if (!cancelled) {
          setAuthed(true);
          loadPending();
          loadTakedowns();
          loadTips();
          loadPartnerImports();
        }
      } catch {
        if (!cancelled) setAuthed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, loadPending, loadTakedowns, loadTips, loadPartnerImports]);

  const signIn = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    try {
      await api.get("/admin/ping", { headers: { "X-Admin-Token": authTry } });
      tokenStore.set(authTry);
      setToken(authTry);
      setAuthed(true);
      toast.success("Signed in.");
    } catch {
      toast.error("Invalid admin token.");
    } finally {
      setAuthLoading(false);
    }
  };

  const signOut = () => {
    tokenStore.clear();
    setToken("");
    setAuthed(false);
    setPending([]);
    setTakedowns([]);
  };

  const ingest = async (e) => {
    e.preventDefault();
    if (mode === "url" && !url.trim()) {
      toast.error("Paste a news article URL.");
      return;
    }
    if (mode === "text" && text.trim().length < 80) {
      toast.error("Paste at least a paragraph of article text.");
      return;
    }
    setIngesting(true);
    try {
      const body = mode === "url" ? { url: url.trim() } : { text };
      const { data } = await api.post("/ingest/article", body, { headers });
      toast.success(
        `Extracted ${data.extracted} case${data.extracted === 1 ? "" : "s"} — ${data.pending} in review.`
      );
      setUrl("");
      setText("");
      loadPending();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Ingest failed — check the URL or token.";
      toast.error(String(msg));
    } finally {
      setIngesting(false);
    }
  };

  const [fbDraft, setFbDraft] = useState(null);

  const approve = async (id) => {
    try {
      const res = await api.post(`/admin/pending/${id}/approve`, {}, { headers });
      const caption = res?.data?.fb_caption;
      if (caption) {
        try { await navigator.clipboard.writeText(caption); } catch (_) {}
        setFbDraft({ caption, recordId: res?.data?.id });
        toast.success("Approved + Facebook caption copied to clipboard.");
      } else {
        toast.success("Approved and published.");
      }
      loadPending();
    } catch {
      toast.error("Approve failed.");
    }
  };

  const reject = async (id) => {
    try {
      await api.delete(`/admin/pending/${id}`, { headers });
      toast.success("Rejected.");
      loadPending();
    } catch {
      toast.error("Reject failed.");
    }
  };

  const runRss = async () => {
    setRssRunning(true);
    try {
      const { data } = await api.post("/admin/run-rss-ingest?max_articles=5", {}, { headers });
      toast.success(
        `RSS: scanned ${data.scanned_feeds} feeds, ${data.new_articles} new articles → ${data.pending_cases} cases pending`
      );
      loadPending();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "RSS run failed");
    } finally {
      setRssRunning(false);
    }
  };

  const takedownAction = async (tid, action) => {
    try {
      await api.post(`/admin/takedowns/${tid}/action?action=${action}`, {}, { headers });
      toast.success(
        action === "remove"
          ? "Record removed."
          : action === "dismiss"
            ? "Dismissed."
            : "Marked under review."
      );
      loadTakedowns();
      loadPending();
    } catch {
      toast.error("Action failed.");
    }
  };

  if (!authed) {
    return (
      <>
        <SEO title="Admin · Sign In" path="/admin" noindex />
        <AdminSignIn
          authTry={authTry}
          setAuthTry={setAuthTry}
          onSubmit={signIn}
          loading={authLoading}
        />
      </>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="admin-page">
      <SEO title="Admin · Review Queue" path="/admin" noindex />
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Section · Admin</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            News Ingestor
            <br />
            & Review Queue
          </h1>
          <p className="text-zinc-700 mt-3 max-w-xl">
            Paste an article URL or text. Claude Sonnet 4.5 extracts every
            convicted individual into a pending record that you approve before
            it appears on the public registry.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <AdminPushToggle />
          <button onClick={signOut} className="btn-outline" data-testid="admin-signout">
            Sign Out
          </button>
        </div>
      </div>

      {/* Photo coverage snapshot */}
      <div className="mt-6">
        <AdminPhotoCoverage token={token} />
      </div>

      {/* Site analytics + subscribers */}
      <div className="mt-6">
        <AdminAnalytics token={token} />
      </div>

      {/* RSS auto-ingest */}
      <div className="mt-8 border border-zinc-900 bg-zinc-900 text-white p-5 flex flex-wrap items-center justify-between gap-4" data-testid="admin-rss-block">
        <div className="flex items-center gap-3">
          <Rss size={18} className="text-red-500" />
          <div>
            <div className="label-eyebrow text-red-500">News auto-ingest</div>
            <div className="font-display font-black text-lg mt-0.5">
              Google News RSS (3 feeds)
            </div>
            <div className="text-xs font-mono text-zinc-400 mt-1">
              Runs nightly at 06:15 UTC when RSS_ENABLED=true. Click below to run once now.
            </div>
          </div>
        </div>
        <button
          onClick={runRss}
          disabled={rssRunning}
          className="bg-red-600 text-white px-5 py-3 text-xs font-bold uppercase tracking-widest border border-red-700 hover:bg-red-700 inline-flex items-center gap-2 disabled:opacity-60"
          data-testid="admin-rss-run"
        >
          {rssRunning ? <Loader2 size={14} className="animate-spin" /> : <Rss size={14} />}
          {rssRunning ? "Scanning feeds..." : "Run RSS Ingest Now"}
        </button>
      </div>

      <AdminTakedownsList
        takedowns={takedowns}
        loading={loadingTakedowns}
        onRefresh={loadTakedowns}
        onAction={takedownAction}
      />

      <div className="mt-10">
        <AdminTipsList
          tips={tips}
          loading={loadingTips}
          onRefresh={loadTips}
          onResolve={resolveTip}
          onClaudeIngest={claudeIngestTip}
          ingestingTipId={ingestingTipId}
        />
        <AdminPartnerImportsList
          imports={partnerImports}
          loading={loadingPartnerImports}
          onRefresh={loadPartnerImports}
        />
      </div>

      <div className="mt-10">
        <AdminUrgentRescuesList token={token} />
      </div>

      <div className="mt-10">
        <AdminActiveCasesList token={token} />
      </div>

      <div className="mt-10">
        <AdminRescuePartnersList token={token} />
      </div>

      <div className="mt-10">
        <AdminSheltersList token={token} />
      </div>

      <div className="mt-10">
        <AdminCampaignsList token={token} />
      </div>

      <div className="mt-10">
        <AdminPhotoEditor headers={headers} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-10">
        {/* Ingest form */}
        <div className="lg:col-span-5">
          <div className="border border-zinc-900 bg-white">
            <div className="px-5 py-3 border-b border-zinc-900 flex items-center gap-2">
              <Sparkles size={14} className="text-red-600" />
              <span className="label-eyebrow">LLM-Powered Ingest</span>
            </div>
            <div className="p-5">
              <div className="flex border border-zinc-300 mb-4">
                {[
                  { k: "url", l: "URL", icon: Link2 },
                  { k: "text", l: "Text", icon: FileText },
                ].map((t) => (
                  <button
                    key={t.k}
                    type="button"
                    onClick={() => setMode(t.k)}
                    className={`flex-1 py-2 text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 ${
                      mode === t.k
                        ? "bg-zinc-900 text-white"
                        : "bg-white text-zinc-900 hover:bg-zinc-100"
                    }`}
                    data-testid={`admin-ingest-mode-${t.k}`}
                  >
                    <t.icon size={12} /> {t.l}
                  </button>
                ))}
              </div>

              <form onSubmit={ingest} className="space-y-4">
                {mode === "url" ? (
                  <div>
                    <label className="label-eyebrow block mb-2">News Article URL</label>
                    <input
                      value={url}
                      onChange={(e) => setUrl(e.target.value)}
                      className="field"
                      placeholder="https://example-news.com/story"
                      data-testid="admin-ingest-url"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="label-eyebrow block mb-2">Article Text</label>
                    <textarea
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      rows={10}
                      className="field font-mono text-sm"
                      placeholder="Paste the article text here..."
                      data-testid="admin-ingest-text"
                    />
                  </div>
                )}
                <button
                  type="submit"
                  disabled={ingesting}
                  className="btn-primary w-full justify-center disabled:opacity-60"
                  data-testid="admin-ingest-submit"
                >
                  {ingesting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {ingesting ? "Extracting..." : "Extract Cases with AI"}
                </button>
              </form>

              <div className="mt-4 border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-600 flex items-start gap-2">
                <AlertTriangle size={14} className="flex-shrink-0 mt-0.5 text-zinc-500" />
                Only <strong>convicted / pleaded guilty / pleaded no contest</strong>{" "}
                individuals are extracted. People only charged or accused are skipped.
                Every extracted case lands in the review queue.
              </div>
            </div>
          </div>

          <div className="mt-6 border border-zinc-300 p-4 bg-zinc-50">
            <div className="label-eyebrow flex items-center gap-2">
              <Newspaper size={12} /> Try a public test article
            </div>
            <div className="text-xs font-mono text-zinc-700 mt-2 break-all">
              https://www.cbsnews.com/detroit/news/woman-sentenced-animal-neglect-37-animals-removed/
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <AdminPendingList
            pending={pending}
            loading={loadingList}
            onRefresh={loadPending}
            onApprove={approve}
            onReject={reject}
          />
        </div>
      </div>

      {fbDraft && (
        <FbDraftModal draft={fbDraft} onClose={() => setFbDraft(null)} />
      )}
    </div>
  );
}

function FbDraftModal({ draft, onClose }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(draft.caption);
      toast.success("Copied again.");
    } catch {
      toast.error("Copy failed — select manually.");
    }
  };
  return (
    <div
      className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
      data-testid="fb-draft-modal"
    >
      <div
        className="bg-white border-2 border-zinc-900 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-zinc-900 text-white px-5 py-4 flex items-center justify-between border-b border-red-600">
          <div>
            <div className="label-eyebrow text-red-500">Approved · Facebook ready</div>
            <div className="font-display font-black text-xl mt-1">
              Paste into your FB Page
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white hover:text-red-500 text-xl leading-none"
            aria-label="Close"
            data-testid="fb-draft-close"
          >
            ×
          </button>
        </div>
        <div className="p-5 text-sm space-y-4">
          <p className="text-zinc-700">
            Record is live on the registry. The caption below was already copied
            to your clipboard — paste it into a new Facebook post, attach an
            image (your cover, a dog photo, or nothing), and hit Publish.
          </p>
          <pre
            className="bg-zinc-50 border border-zinc-300 p-3 text-xs whitespace-pre-wrap font-sans max-h-64 overflow-y-auto"
            data-testid="fb-draft-caption"
          >
            {draft.caption}
          </pre>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copy}
              className="btn-outline text-xs"
              data-testid="fb-draft-copy"
            >
              Copy again
            </button>
            <a
              href="https://www.facebook.com/PawsOnRecord"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 bg-zinc-900 text-white border border-zinc-900 hover:bg-red-600 hover:border-red-600 px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors"
              data-testid="fb-draft-open-fb"
            >
              Open Facebook Page ↗
            </a>
            <button
              type="button"
              onClick={onClose}
              className="ml-auto text-xs font-bold uppercase tracking-widest hover:text-red-600"
              data-testid="fb-draft-dismiss"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
